<script setup>
import { ref, computed, watch } from 'vue';
import { Card } from 'primevue';
import InputNumber from 'primevue/inputnumber';
import ANButton from '@/components/common/AnButton.vue';
import Chart from 'primevue/chart';
import { calculateSIP } from '@/utils/financeCalculators.js';

// Form data
const formData = ref({
  sip: 5000,
  finalAmount: null,
  durationYears: 10,
  rate: 12
});

const calculationMode = ref('calculateFuture'); // 'calculateFuture' or 'calculateSIP'
const result = ref(null);
const chartData = ref({});
const chartOptions = ref({});

// Computed properties
const isValidInput = computed(() => {
  if (calculationMode.value === 'calculateFuture') {
    return formData.value.sip > 0 && formData.value.durationYears > 0 && formData.value.rate > 0;
  } else {
    return formData.value.finalAmount > 0 && formData.value.durationYears > 0 && formData.value.rate > 0;
  }
});

// Calculate SIP
const calculate = () => {
  if (!isValidInput.value) return;

  const input = {
    sip: calculationMode.value === 'calculateFuture' ? formData.value.sip : null,
    finalAmount: calculationMode.value === 'calculateSIP' ? formData.value.finalAmount : null,
    durationYears: formData.value.durationYears,
    rate: formData.value.rate
  };

  result.value = calculateSIP(input);
  if (!result.value.error) {
    generateChart();
    // Scroll to results section after calculation
    setTimeout(() => {
      const resultsElement = document.querySelector('.result-card');
      if (resultsElement) {
        resultsElement.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'start',
          inline: 'nearest' 
        });
      }
    }, 100);
  }
};

// Generate chart data
const generateChart = () => {
  const years = [];
  const investedAmounts = [];
  const futureValues = [];
  
  const monthlyAmount = calculationMode.value === 'calculateFuture' ? 
    formData.value.sip : result.value.sipRequired;
  const r = formData.value.rate / 100 / 12;

  for (let year = 1; year <= formData.value.durationYears; year++) {
    const months = year * 12;
    const invested = monthlyAmount * months;
    const futureValue = monthlyAmount * (((1 + r) ** months - 1) * (1 + r)) / r;
    
    years.push(`Year ${year}`);
    investedAmounts.push(invested);
    futureValues.push(futureValue);
  }

  chartData.value = {
    labels: years,
    datasets: [
      {
        label: 'Amount Invested',
        data: investedAmounts,
        backgroundColor: 'rgba(54, 162, 235, 0.6)',
        borderColor: 'rgba(54, 162, 235, 1)',
        borderWidth: 2,
        fill: false
      },
      {
        label: 'Future Value',
        data: futureValues,
        backgroundColor: 'rgba(255, 99, 132, 0.6)',
        borderColor: 'rgba(255, 99, 132, 1)',
        borderWidth: 2,
        fill: false
      }
    ]
  };

  chartOptions.value = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'SIP Investment Growth Over Time'
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: function(value) {
            return '₹' + (value / 100000).toFixed(1) + 'L';
          }
        }
      }
    }
  };
};

// Watch for mode change and clear results
watch(calculationMode, () => {
  result.value = null;
  chartData.value = {};
});

// Initialize
calculate();
</script>

<template>
  <div class="sip-calculator">
    <div class="calculator-grid">
      <!-- Input Form -->
      <Card class="input-card">
        <template #title>
          <div class="card-header">
            <h3>SIP Calculator Input</h3>
            <div class="mode-toggle">
              <button 
                :class="['mode-btn', { active: calculationMode === 'calculateFuture' }]"
                @click="calculationMode = 'calculateFuture'"
              >
                Calculate Future Value
              </button>
              <button 
                :class="['mode-btn', { active: calculationMode === 'calculateSIP' }]"
                @click="calculationMode = 'calculateSIP'"
              >
                Calculate Required SIP
              </button>
            </div>
          </div>
        </template>
        <template #content>
          <div class="form-fields">
            <!-- SIP Amount (for Future Value calculation) -->
            <div v-if="calculationMode === 'calculateFuture'" class="field">
              <label for="sip">Monthly SIP Amount (₹)</label>
              <InputNumber
                id="sip"
                v-model="formData.sip"
                :min="100"
                :max="1000000"
                :step="500"
                showButtons
                buttonLayout="horizontal"
                decrementButtonClass="p-button-secondary"
                incrementButtonClass="p-button-secondary"
                class="input-field"
              />
            </div>

            <!-- Final Amount (for SIP calculation) -->
            <div v-if="calculationMode === 'calculateSIP'" class="field">
              <label for="finalAmount">Target Amount (₹)</label>
              <InputNumber
                id="finalAmount"
                v-model="formData.finalAmount"
                :min="10000"
                :max="100000000"
                :step="10000"
                showButtons
                buttonLayout="horizontal"
                decrementButtonClass="p-button-secondary"
                incrementButtonClass="p-button-secondary"
                class="input-field"
              />
            </div>

            <!-- Duration -->
            <div class="field">
              <label for="duration">Investment Duration (Years)</label>
              <InputNumber
                id="duration"
                v-model="formData.durationYears"
                :min="1"
                :max="50"
                :step="1"
                showButtons
                buttonLayout="horizontal"
                decrementButtonClass="p-button-secondary"
                incrementButtonClass="p-button-secondary"
                class="input-field"
              />
            </div>

            <!-- Rate -->
            <div class="field">
              <label for="rate">Expected Annual Return (%)</label>
              <InputNumber
                id="rate"
                v-model="formData.rate"
                :min="1"
                :max="30"
                :step="0.5"
                :minFractionDigits="1"
                :maxFractionDigits="2"
                showButtons
                buttonLayout="horizontal"
                decrementButtonClass="p-button-secondary"
                incrementButtonClass="p-button-secondary"
                class="input-field"
              />
            </div>

            <!-- Calculate Button -->
            <ANButton
              label="Calculate"
              severity="primary"
              size="large"
              @click="calculate"
              :disabled="!isValidInput"
              class="calculate-btn"
            />
          </div>
        </template>
      </Card>

      <!-- Results -->
      <Card v-if="result && !result.error" class="result-card">
        <template #title>
          <h3>SIP Calculation Results</h3>
        </template>
        <template #content>
          <div class="results">
            <div v-if="calculationMode === 'calculateFuture'" class="result-item highlight">
              <div class="result-label">Future Value</div>
              <div class="result-value">₹{{ result.futureValue.toLocaleString('en-IN') }}</div>
            </div>

            <div v-if="calculationMode === 'calculateSIP'" class="result-item highlight">
              <div class="result-label">Required Monthly SIP</div>
              <div class="result-value">₹{{ result.sipRequired.toLocaleString('en-IN') }}</div>
            </div>

            <div class="result-summary">
              <div class="summary-item">
                <span class="summary-label">Total Investment:</span>
                <span class="summary-value">
                  ₹{{ ((calculationMode === 'calculateFuture' ? formData.sip : result.sipRequired) * formData.durationYears * 12).toLocaleString('en-IN') }}
                </span>
              </div>
              <div class="summary-item">
                <span class="summary-label">Wealth Gain:</span>
                <span class="summary-value gain">
                  ₹{{ (
                    (calculationMode === 'calculateFuture' ? result.futureValue : formData.finalAmount) -
                    ((calculationMode === 'calculateFuture' ? formData.sip : result.sipRequired) * formData.durationYears * 12)
                  ).toLocaleString('en-IN') }}
                </span>
              </div>
            </div>
          </div>
        </template>
      </Card>
    </div>

    <!-- Chart -->
    <Card v-if="result && !result.error && chartData.labels" class="chart-card">
      <template #title>
        <h3>SIP Growth Visualization</h3>
      </template>
      <template #content>
        <div class="chart-container">
          <Chart type="line" :data="chartData" :options="chartOptions" class="chart" />
        </div>
      </template>
    </Card>

    <!-- Error Display -->
    <Card v-if="result && result.error" class="error-card">
      <template #content>
        <div class="error-message">
          <i class="pi pi-exclamation-triangle"></i>
          <span>{{ result.error }}</span>
        </div>
      </template>
    </Card>
  </div>
</template>

<style scoped>
.sip-calculator {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.calculator-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
}

.card-header {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.card-header h3 {
  margin: 0;
  color: var(--an-text-primary);
}

.mode-toggle {
  display: flex;
  gap: 8px;
}

.mode-btn {
  padding: 8px 16px;
  border: 2px solid var(--an-primary);
  background: var(--an-white);
  color: var(--an-primary);
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 14px;
  font-weight: 500;
}

.mode-btn.active {
  background: var(--an-primary);
  color: var(--an-white);
}

.mode-btn:hover:not(.active) {
  background: var(--an-background-light);
}

.form-fields {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.field label {
  font-weight: 600;
  color: var(--an-text-primary);
  font-size: 14px;
}

.input-field {
  width: 100%;
}

.calculate-btn {
  width: 100%;
  margin-top: 16px;
}

.results {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.result-item {
  text-align: center;
  padding: 20px;
  border-radius: 8px;
  background: var(--an-background-light);
}

.result-item.highlight {
  background: linear-gradient(135deg, var(--an-primary), var(--an-secondary));
  color: var(--an-white);
}

.result-label {
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 8px;
  opacity: 0.9;
}

.result-value {
  font-size: 2rem;
  font-weight: 700;
}

.result-summary {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.summary-item {
  display: flex;
  justify-content: space-between;
  padding: 12px 0;
  border-bottom: 1px solid var(--an-gray3);
}

.summary-label {
  color: var(--an-text-secondary);
  font-weight: 500;
}

.summary-value {
  font-weight: 600;
  color: var(--an-text-primary);
}

.summary-value.gain {
  color: var(--an-success);
}

.chart-card {
  width: 100%;
}

.chart-container {
  height: 400px;
  width: 100%;
}

.chart {
  height: 100% !important;
  width: 100% !important;
}

.error-card {
  background: var(--an-error-light);
  border: 1px solid var(--an-error);
}

.error-message {
  display: flex;
  align-items: center;
  gap: 12px;
  color: var(--an-error);
  font-weight: 500;
}

/* Responsive Design */
@media (max-width: 768px) {
  .calculator-grid {
    grid-template-columns: 1fr;
  }
  
  .mode-toggle {
    flex-direction: column;
  }
  
  .mode-btn {
    text-align: center;
  }
  
  .result-value {
    font-size: 1.5rem;
  }
  
  .chart-container {
    height: 300px;
  }
}
</style>
